// DS6.20
import React, { useId } from 'react';
import isEqual from 'lodash/isEqual';
import { useDropdownItems } from '../../hooks/useDropdownItems';
import { StandardMenuProps } from '../../MenuPreset/StandardMenu';
import { NullableDropdownItem } from '../../types';
import StandardDropdownMenu from '../StandardDropdown/StandardDropdownMenu';
import {
  PopperDropdownManagerProps,
  PopperDropdownManager,
} from '../../PopperDropdownLayout';
import PopperBrandedToggleButton, {
  PopperBrandedToggleButtonProps,
} from '../../toggles/PopperBrandedToggleButton';

export interface Props
  extends Omit<PopperBrandedToggleButtonProps, 'aria-haspopup'> {
  items: NullableDropdownItem[];

  popperManagerProps?: PopperDropdownManagerProps;
  standardMenuProps?: Omit<
    StandardMenuProps,
    'items' | 'role' | 'id' | 'menuScrollable'
  >;
}

/**
 * Our DS `BrandedButton` with a context menu provided by our dropdown library.
 *
 * You can customize the popper or standarded menu instance via `popperManagerProps` or `standardMenuProps` respectively.
 */
const BrandedButtonWithContextMenu: React.FC<Props> = React.memo(
  ({ items, popperManagerProps, standardMenuProps, ...rest }) => {
    const { filteredItems } = useDropdownItems(items);

    const toggleId = useId();
    const dropdownId = useId();

    return (
      <PopperDropdownManager {...popperManagerProps}>
        <PopperBrandedToggleButton
          id={toggleId}
          aria-haspopup="menu"
          aria-controls={dropdownId}
          {...rest}
        />
        <StandardDropdownMenu
          id={dropdownId}
          role="menu"
          aria-labelledby={toggleId}
          menuScrollable
          items={filteredItems}
          {...standardMenuProps}
        />
      </PopperDropdownManager>
    );
  },
  // Deep equal from lodash so we can compare the standard menu and popper manager prop collections
  isEqual
);
BrandedButtonWithContextMenu.displayName = 'BrandedButtonWithContextMenu';

export default BrandedButtonWithContextMenu;
